import { useCallback, useMemo } from "react";
import {
  GridApiPro,
  GridColDef,
  GridColumnOrderChangeParams,
  GridColumnResizeParams,
} from "@mui/x-data-grid-pro";

import { useCustomLocalStorageState } from "shared/hooks";

interface UseColumnPersistenceOptions {
  pageKey: string;
  apiRef: React.RefObject<GridApiPro | null>;
}

interface UseColumnPersistenceReturn {
  columns: GridColDef[];
  handleColumnOrderChange: (params: GridColumnOrderChangeParams) => void;
  handleColumnWidthChange: (params: GridColumnResizeParams) => void;
}

// Internal columns added by DataGridPro start with "__" and should be excluded from order tracking
const isInternalColumn = (field: string) => field.startsWith("__");

/**
 * Custom hook for managing column order and width persistence in MaterialTable components
 * @param baseColumns - The base column definitions
 * @param options - Configuration options including pageKey and apiRef
 * @returns Object containing processed columns and event handlers
 */
export const useColumnPersistence = (
  baseColumns: GridColDef[],
  options: UseColumnPersistenceOptions
): UseColumnPersistenceReturn => {
  const { pageKey, apiRef } = options;

  // Generate storage keys based on pageKey
  const storageKeyBase = pageKey || "default";

  const [columnOrder, setColumnOrder] = useCustomLocalStorageState<string[]>(
    `${storageKeyBase}-column-order`,
    {
      defaultValue: [],
    }
  );

  const [columnWidths, setColumnWidths] = useCustomLocalStorageState<
    Record<string, number>
  >(`${storageKeyBase}-column-widths`, {
    defaultValue: {},
  });

  const columns: GridColDef[] = useMemo(() => {
    let processedColumns = baseColumns;

    // Apply stored column widths to base columns
    if (Object.keys(columnWidths).length > 0) {
      processedColumns = processedColumns.map((col) => ({
        ...col,
        width: columnWidths[col.field] || col.width,
      }));
    }

    // If no custom column order is stored, return columns with widths applied
    if (!columnOrder.length) {
      return processedColumns;
    }

    // Reorder columns based on stored order
    const columnMap = new Map(processedColumns.map((col) => [col.field, col]));

    // Build ordered columns array following the stored order
    const orderedColumns: GridColDef[] = [];
    columnOrder.forEach((fieldName) => {
      const column = columnMap.get(fieldName);
      if (column) {
        orderedColumns.push(column);
        columnMap.delete(fieldName);
      }
    });

    // Append any remaining columns that weren't in the stored order
    columnMap.forEach((column) => {
      orderedColumns.push(column);
    });

    return orderedColumns;
  }, [baseColumns, columnOrder, columnWidths]);

  const handleColumnOrderChange = useCallback(
    (params: GridColumnOrderChangeParams) => {
      // Ignore internal columns
      if (isInternalColumn(params.column.field)) {
        return;
      }

      // Use setTimeout to defer reading until after the grid has fully updated its state
      setTimeout(() => {
        if (apiRef.current) {
          const allColumns = apiRef.current.getAllColumns();
          const newOrder = allColumns
            .map((col) => col.field)
            .filter((field) => !isInternalColumn(field));
          setColumnOrder(newOrder);
        }
      }, 0);
    },
    [apiRef, setColumnOrder]
  );

  const handleColumnWidthChange = (params: GridColumnResizeParams) => {
    // Update the stored width for the resized column
    // This merges the new width into the existing widths object
    setColumnWidths((prevWidths) => ({
      ...prevWidths,
      [params.colDef.field]: params.width,
    }));
  };

  return {
    columns,
    handleColumnOrderChange,
    handleColumnWidthChange,
  };
};
